import { useCallback, useEffect, useState } from 'react';
import { useNavigation } from '@react-navigation/native';
import { useAppDispatch, useAppSelector } from '@/store';
import { selectSortedEvents } from '@/store/selectors/residentsEventsSelectors';
import { fetchHomeData } from '@/store/slices/residents/homeSlice';
import { fetchWifiInfo } from '@/store/slices/residents/wifiSlice';
import { usePaymentBalance } from '@/features/payments/api/paymentHooks';
import { useMaintenanceTickets } from '@/hooks/residents/useMaintenanceTickets';
import { CommunityResource } from '@/types/residents/communityResource';
import { Event as ResioEvent } from '@/types/residents/event';

export interface UseHomeScreenReturn {
  // User & Auth
  user: any;
  
  // Data
  resources: CommunityResource[];
  linkedAccounts: any[];
  unreadBulletinsCount: number;
  validEvents: ResioEvent[];
  wifiInfo: any;
  balanceDue: number;
  openTickets: any[];
  
  // Loading states
  isPaymentLoading: boolean;
  isMaintenanceLoading: boolean;
  refreshing: boolean;
  
  // Computed states
  shouldShowPayments: boolean;
  shouldShowEventsSection: boolean;
  shouldShowWiFiCard: boolean;
  
  // Handlers
  handleRefresh: () => Promise<void>;
  handleBulletinPress: () => void;
  handleEventPress: (event: ResioEvent) => void;
  handleWiFiPress: () => void;
  handleResourcePress: (resource?: CommunityResource) => void;
  handleLinkedAccountsPress: () => void;
  handleMarketplacePress: () => void;
  handlePaymentsPress: () => void;
  handleMakePayment: () => void;
  handleMaintenancePress: () => void;
  handleMessagesPress: () => void;
}

/**
 * useHomeScreen - Shared hook for home screen business logic
 * 
 * Extracts all data fetching, state management, and navigation handlers
 * to be shared between mobile and desktop home screen views.
 */
export const useHomeScreen = (): UseHomeScreenReturn => {
  const navigation = useNavigation<any>();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { resources, linkedAccounts, unreadBulletinsCount } = useAppSelector((state) => state.home);
  const events = useAppSelector(selectSortedEvents);
  const { wifiInfo } = useAppSelector((state) => state.wifi);
  
  // Refresh state
  const [refreshing, setRefreshing] = useState(false);
  
  // Payment data hook
  const leaseId = user?.currentLeaseId ? String(user.currentLeaseId) : '';
  const { balanceDue, isLoading: isPaymentLoading } = usePaymentBalance(leaseId);
  
  // Maintenance tickets hook
  const { openTickets, loading: isMaintenanceLoading, refresh: refreshMaintenance } = useMaintenanceTickets();
  
  // Hide Payments for Non-Leasing Occupants
  const shouldShowPayments = user?.customerType !== 'non-leasing occupant';
  
  // Check for undefined events and filter valid events
  const validEvents = events.filter((event) => event && event.id);
  
  // iOS behavior: Hide sections when empty/unavailable
  const shouldShowEventsSection = validEvents.length > 0;
  const shouldShowWiFiCard = wifiInfo !== null;

  // Fetch home data when component mounts or when user changes
  useEffect(() => {
    if (user?.propertyId) {
      dispatch(fetchHomeData(user.propertyId));
    }
    // Fetch WiFi info if lease ID is available and not already loaded
    if (user?.currentLeaseId && !wifiInfo) {
      dispatch(fetchWifiInfo(user.currentLeaseId));
    }
  }, [dispatch, user?.propertyId, user?.currentLeaseId, wifiInfo]);

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      if (user?.propertyId) {
        await dispatch(fetchHomeData(user.propertyId));
      }
      refreshMaintenance();
    } finally {
      setRefreshing(false);
    }
  }, [dispatch, user?.propertyId, refreshMaintenance]);

  // Navigation handlers
  const handleBulletinPress = useCallback(() => {
    navigation.navigate('ResidentNotifications');
  }, [navigation]);

  const handleEventPress = useCallback((event: ResioEvent) => {
    navigation.navigate('EventDetail', { event });
  }, [navigation]);

  const handleWiFiPress = useCallback(() => {
    navigation.navigate('WifiConnection');
  }, [navigation]);

  const handleResourcePress = useCallback((resource?: CommunityResource) => {
    if (resource) {
      navigation.navigate('MobileResourceDetail', { resource });
    } else {
      navigation.navigate('ResidentResources');
    }
  }, [navigation]);

  const handleLinkedAccountsPress = useCallback(() => {
    navigation.navigate('ResidentLinkedAccounts');
  }, [navigation]);

  const handleMarketplacePress = useCallback(() => {
    navigation.navigate('ResidentMarketplace');
  }, [navigation]);

  const handlePaymentsPress = useCallback(() => {
    navigation.navigate('ResidentPayments');
  }, [navigation]);

  const handleMakePayment = useCallback(() => {
    if (user?.currentLeaseId) {
      navigation.navigate('MakePayment', { leaseId: user.currentLeaseId });
    } else {
      navigation.navigate('ResidentPayments');
    }
  }, [navigation, user?.currentLeaseId]);

  const handleMaintenancePress = useCallback(() => {
    navigation.navigate('ResidentMaintenance');
  }, [navigation]);

  const handleMessagesPress = useCallback(() => {
    navigation.navigate('ResidentMessages');
  }, [navigation]);

  return {
    // User & Auth
    user,
    
    // Data
    resources: resources || [],
    linkedAccounts: linkedAccounts || [],
    unreadBulletinsCount: unreadBulletinsCount || 0,
    validEvents,
    wifiInfo,
    balanceDue,
    openTickets: openTickets || [],
    
    // Loading states
    isPaymentLoading,
    isMaintenanceLoading,
    refreshing,
    
    // Computed states
    shouldShowPayments,
    shouldShowEventsSection,
    shouldShowWiFiCard,
    
    // Handlers
    handleRefresh,
    handleBulletinPress,
    handleEventPress,
    handleWiFiPress,
    handleResourcePress,
    handleLinkedAccountsPress,
    handleMarketplacePress,
    handlePaymentsPress,
    handleMakePayment,
    handleMaintenancePress,
    handleMessagesPress,
  };
};

export default useHomeScreen;
